<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { getDocument, GlobalWorkerOptions, type PDFDocumentProxy } from 'pdfjs-dist'
import pdfWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url'
import { useNamespace } from '@zoryn/hooks'

GlobalWorkerOptions.workerSrc = pdfWorker

interface PdfThumb {
  page: number
  url: string
}

const props = defineProps<{
  url: string
}>()

const ns = useNamespace('ai-pdf-preview')

const scrollRef = ref<HTMLElement | null>(null)
const pagesHost = ref<HTMLElement | null>(null)
const loading = ref(false)
const error = ref('')
const pageCount = ref(0)
const currentPage = ref(1)
const scale = ref(1.15)
const sidebarOpen = ref(true)
const fullscreen = ref(false)
const thumbs = ref<PdfThumb[]>([])
const thumbsLoading = ref(false)

let pdfDoc: PDFDocumentProxy | null = null
let renderToken = 0
let thumbToken = 0

async function destroyDoc(): Promise<void> {
  if (pdfDoc) {
    try {
      await pdfDoc.destroy()
    } catch {
      // ignore
    }
    pdfDoc = null
  }
}

function clearPages(): void {
  if (pagesHost.value) pagesHost.value.innerHTML = ''
}

function clearThumbs(): void {
  for (const t of thumbs.value) {
    if (t.url.startsWith('blob:')) URL.revokeObjectURL(t.url)
  }
  thumbs.value = []
}

function canvasToBlobUrl(canvas: HTMLCanvasElement): Promise<string> {
  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => {
        resolve(blob ? URL.createObjectURL(blob) : '')
      },
      'image/jpeg',
      0.75
    )
  })
}

async function renderPages(): Promise<void> {
  if (!pdfDoc || !pagesHost.value) return
  const token = ++renderToken
  clearPages()

  const host = pagesHost.value
  for (let pageNum = 1; pageNum <= pdfDoc.numPages; pageNum += 1) {
    if (token !== renderToken) return
    const page = await pdfDoc.getPage(pageNum)
    if (token !== renderToken) return

    const viewport = page.getViewport({ scale: scale.value })
    const wrap = document.createElement('div')
    wrap.className = ns.e('page')
    wrap.dataset.page = String(pageNum)

    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')
    if (!context) continue

    const outputScale = window.devicePixelRatio || 1
    canvas.width = Math.floor(viewport.width * outputScale)
    canvas.height = Math.floor(viewport.height * outputScale)
    canvas.style.width = `${Math.floor(viewport.width)}px`
    canvas.style.height = `${Math.floor(viewport.height)}px`

    wrap.appendChild(canvas)
    host.appendChild(wrap)

    await page.render({
      canvasContext: context,
      viewport,
      transform: outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : undefined
    }).promise
  }
}

async function renderThumbnails(): Promise<void> {
  if (!pdfDoc) return
  const token = ++thumbToken
  clearThumbs()
  thumbsLoading.value = true

  try {
    const next: PdfThumb[] = []
    for (let pageNum = 1; pageNum <= pdfDoc.numPages; pageNum += 1) {
      if (token !== thumbToken) return
      const page = await pdfDoc.getPage(pageNum)
      if (token !== thumbToken) return

      const viewport = page.getViewport({ scale: 0.28 })
      const canvas = document.createElement('canvas')
      const context = canvas.getContext('2d')
      if (!context) continue

      canvas.width = Math.floor(viewport.width)
      canvas.height = Math.floor(viewport.height)
      await page.render({ canvasContext: context, viewport }).promise
      if (token !== thumbToken) return

      const url = await canvasToBlobUrl(canvas)
      if (!url) continue
      next.push({ page: pageNum, url })
      // Progressive update so sidebar fills while remaining pages render
      thumbs.value = next.slice()
    }
  } finally {
    if (token === thumbToken) thumbsLoading.value = false
  }
}

async function loadPdf(): Promise<void> {
  loading.value = true
  error.value = ''
  pageCount.value = 0
  currentPage.value = 1
  clearPages()
  clearThumbs()
  await destroyDoc()

  try {
    const response = await fetch(props.url)
    if (!response.ok) {
      throw new Error(`HTTP ${response.status} ${response.statusText || ''}`.trim())
    }
    const data = new Uint8Array(await response.arrayBuffer())
    const task = getDocument({ data })
    pdfDoc = await task.promise
    pageCount.value = pdfDoc.numPages
    await nextTick()
    await renderPages()
    void renderThumbnails()
  } catch (err) {
    error.value = err instanceof Error ? err.message : String(err)
  } finally {
    loading.value = false
  }
}

function zoomIn(): void {
  scale.value = Math.min(3, Number((scale.value + 0.15).toFixed(2)))
}

function zoomOut(): void {
  scale.value = Math.max(0.5, Number((scale.value - 0.15).toFixed(2)))
}

function resetZoom(): void {
  scale.value = 1.15
}

function toggleFullscreen(): void {
  fullscreen.value = !fullscreen.value
}

function exitFullscreen(): void {
  fullscreen.value = false
}

function goToPage(page: number): void {
  const el = pagesHost.value?.querySelector<HTMLElement>(`[data-page="${page}"]`)
  if (!el || !scrollRef.value) return
  el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  currentPage.value = page
}

function onScroll(): void {
  const root = scrollRef.value
  if (!root || !pagesHost.value) return
  const pages = Array.from(pagesHost.value.querySelectorAll<HTMLElement>('.pdf-page'))
  if (!pages.length) return

  const mid = root.scrollTop + root.clientHeight / 3
  let active = 1
  for (const page of pages) {
    const top = page.offsetTop
    const bottom = top + page.offsetHeight
    const num = Number(page.dataset.page || 1)
    if (mid >= top && mid < bottom) {
      active = num
      break
    }
    if (mid >= bottom) active = num
  }
  currentPage.value = active
}

function onKeydown(event: KeyboardEvent): void {
  const target = event.target as HTMLElement | null
  if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return

  if (event.key === 'Escape' && fullscreen.value) {
    event.preventDefault()
    exitFullscreen()
    return
  }

  if (!(event.ctrlKey || event.metaKey)) return
  if (event.key === '=' || event.key === '+') {
    event.preventDefault()
    zoomIn()
  } else if (event.key === '-') {
    event.preventDefault()
    zoomOut()
  } else if (event.key === '0') {
    event.preventDefault()
    resetZoom()
  }
}

watch(
  () => props.url,
  () => {
    void loadPdf()
  },
  { immediate: true }
)

watch(scale, () => {
  if (!pdfDoc) return
  void renderPages()
})

onMounted(() => window.addEventListener('keydown', onKeydown))
onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown)
  renderToken += 1
  thumbToken += 1
  clearPages()
  clearThumbs()
  void destroyDoc()
})
</script>

<template>
  <div
    :class="[ns.b(), ns.is('fullscreen', fullscreen), ns.is('sidebar-open', sidebarOpen)]"
  >
    <div :class="ns.e('toolbar')">
      <div :class="ns.e('toolbar-left')">
        <button
          type="button"
          :class="ns.e('tool')"
          :title="sidebarOpen ? '隐藏缩略图' : '显示缩略图'"
          :aria-pressed="sidebarOpen"
          @click="sidebarOpen = !sidebarOpen"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M4 6h16M4 12h16M4 18h10"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
            />
          </svg>
        </button>
        <span :class="ns.e('page-label')">{{ currentPage }} / {{ pageCount || '—' }}</span>
      </div>

      <div :class="ns.e('toolbar-right')">
        <div :class="ns.e('zoom')">
          <button
            type="button"
            :class="ns.e('tool')"
            title="缩小 (Ctrl -)"
            :disabled="loading || scale <= 0.5"
            @click="zoomOut"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="10.5" cy="10.5" r="6.5" fill="none" stroke="currentColor" stroke-width="1.8" />
              <path d="M15.5 15.5 20 20M8 10.5h5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
            </svg>
          </button>
          <button type="button" :class="ns.e('zoom-label')" title="重置缩放" :disabled="loading" @click="resetZoom">
            {{ Math.round(scale * 100) }}%
          </button>
          <button
            type="button"
            :class="ns.e('tool')"
            title="放大 (Ctrl +)"
            :disabled="loading || scale >= 3"
            @click="zoomIn"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="10.5" cy="10.5" r="6.5" fill="none" stroke="currentColor" stroke-width="1.8" />
              <path
                d="M15.5 15.5 20 20M10.5 8v5M8 10.5h5"
                fill="none"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
              />
            </svg>
          </button>
        </div>

        <button
          type="button"
          :class="ns.e('tool')"
          :title="fullscreen ? '退出全屏 (Esc)' : '全屏'"
          :aria-pressed="fullscreen"
          @click="toggleFullscreen"
        >
          <svg v-if="!fullscreen" viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M8 4H4v4M16 4h4v4M8 20H4v-4M16 20h4v-4"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <svg v-else viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M9 9H5V5M15 9h4V5M9 15H5v4M15 15h4v4"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </div>
    </div>

    <div :class="ns.e('body')">
      <aside v-show="sidebarOpen" :class="ns.e('thumbs')" aria-label="页面缩略图">
        <p v-if="thumbsLoading && !thumbs.length" :class="ns.e('thumbs-hint')">生成缩略图…</p>
        <button
          v-for="t in thumbs"
          :key="t.page"
          type="button"
          :class="[ns.e('thumb'), ns.is('active', currentPage === t.page)]"
          :title="`第 ${t.page} 页`"
          @click="goToPage(t.page)"
        >
          <img :src="t.url" :alt="`第 ${t.page} 页`" />
          <span :class="ns.e('thumb-num')">{{ t.page }}</span>
        </button>
      </aside>

      <div ref="scrollRef" :class="ns.e('scroll')" @scroll.passive="onScroll">
        <p v-if="loading" :class="ns.e('status')">正在渲染 PDF…</p>
        <p v-else-if="error" :class="[ns.e('status'), ns.is('error')]">预览失败：{{ error }}</p>
        <div ref="pagesHost" :class="ns.e('pages')" />
      </div>
    </div>
  </div>
</template>
